import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Sort, SortDirection } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { FwTooltipDirective } from '@flywheel-io/vision';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

export interface AcquisitionSortValue extends Record<string, Sort> {
  acquisition: {
    active: 'label' | 'created' | 'timestamp';
    direction: SortDirection;
  };
  file: {
    active: 'description' | 'created' | 'classification' | 'fileType';
    direction: SortDirection;
  };
}

const ACQUISITION_SORT_LABELS: Record<AcquisitionSortValue['acquisition']['active'], string> = {
  label: 'Label',
  created: 'Created Date',
  timestamp: 'Timestamp',
};

const FILE_SORT_LABELS: Record<AcquisitionSortValue['file']['active'], string> = {
  description: 'Description',
  created: 'Created Date',
  classification: 'Classification',
  fileType: 'Type',
};

@Component({
  selector: 'acquisition-sort',
  styleUrls: ['./acquisition-sort.component.scss'],
  templateUrl: './acquisition-sort.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => AcquisitionSortComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwTooltipDirective,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
})
export class AcquisitionSortComponent implements ControlValueAccessor {
  readonly disabled$ = new BehaviorSubject(false);
  readonly options = [
    {
      value: 'acquisition',
      options: [{ value: 'label' }, { value: 'created' }, { value: 'timestamp' }],
    },
    {
      value: 'file',
      options: [{ value: 'description' }, { value: 'created' }, { value: 'classification' }, { value: 'fileType' }],
    },
  ] as const;
  readonly tooltip$: Observable<string>;
  readonly value$ = new BehaviorSubject<AcquisitionSortValue>({
    acquisition: {
      active: 'created',
      direction: 'asc',
    },
    file: {
      active: 'created',
      direction: 'asc',
    },
  });

  readonly sortCategoryTitles = {
    label: 'Label',
    description: 'Description',
    created: 'Created Date',
    timestamp: 'Timestamp',
    classification: 'Classification',
    fileType: 'Type',
  } as const;

  private onChange: (value: AcquisitionSortValue) => void = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function

  constructor() {
    this.tooltip$ = this.value$.pipe(
      map((value) => {
        const acquisitionField = ACQUISITION_SORT_LABELS[value.acquisition.active];
        const acquisitionDirection = value.acquisition.direction === 'desc' ? 'Descending' : 'Ascending';
        const fileField = FILE_SORT_LABELS[value.file.active];
        const fileDirection = value.file.direction === 'desc' ? 'Descending' : 'Ascending';

        return `Sort by: Acquisition ${acquisitionField} ${acquisitionDirection}, File ${fileField} ${fileDirection}`;
      }),
    );
  }

  get value(): AcquisitionSortValue {
    return this.value$.value;
  }

  openSortMenu(): void {
    this.onTouched();
  }

  selectSortOption(level: keyof AcquisitionSortValue, active: string): void {
    if (this.disabled$.value) {
      return;
    }
    this.value$.next({
      ...this.value,
      [level]: {
        direction:
          this.value[level].active === active && this.value[level].direction === 'asc'
            ? 'desc'
            : ('asc' as SortDirection),
        active: active,
      },
    });
    this.onChange(this.value);
  }

  writeValue(value: AcquisitionSortValue): void {
    this.value$.next(value);
  }

  registerOnChange(fn: (value: AcquisitionSortValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled$.next(isDisabled);
  }
}
